import { ChangeEvent, useRef, useState } from "react";

import {
  CloudUpload as CloudUploadIcon,
  DeleteRounded as DeleteRoundedIcon,
  FilePresentRounded as FilePresentRoundedIcon,
} from "@mui/icons-material";
import { Box, Button, CircularProgress, colors, Tooltip, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import { AttachmentType } from "@interfaces/campaign.type";

import { PopupActions } from "@store/popupStore";

import theme from "@theme/index";

// hidden input for file upload
const VisuallyHiddenInput = styled("input")({
  bottom: 0,
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  left: 0,
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
});

const truncateFilename = (filename: string) => {
  if (filename.length > 15) {
    return `${filename.slice(0, 10)}...${filename.slice(Math.max(0, filename.length - 10))}`;
  }
  return filename;
};

interface IAttachmentsManagerProperties {
  attachments: AttachmentType[];
  deleteAttachment: (attachmentId: number, attachmentName: string) => Promise<void>;
  isDisabled?: boolean;
  uploadAttachment: (file: File[]) => Promise<void>;
}

function AttachmentsManager({
  attachments = [],
  deleteAttachment,
  isDisabled,
  uploadAttachment,
}: IAttachmentsManagerProperties) {
  const [isUploading, setIsUploading] = useState(false);
  const [deleteAttachmentIds, setDeleteAttachmentIds] = useState<number[]>([]);
  const inputReference = useRef<HTMLInputElement>(null);

  const removeAttachment = async (attachment: AttachmentType) => {
    setDeleteAttachmentIds([...deleteAttachmentIds, attachment.id]);
    await deleteAttachment(attachment.id, attachment.name);
    setDeleteAttachmentIds(deleteAttachmentIds.filter((id) => id !== attachment.id));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setIsUploading(true);
    const newFiles = [...(event.target.files ?? [])];
    const filesWithinSizeLimit: File[] = [];
    const filesOutsideSizeLimit: File[] = [];
    const maxCombinedSize = 3_000_000;
    let totalSize = attachments.reduce((accumulator, attachment) => accumulator + attachment.size, 0);
    for (const file of newFiles) {
      if (totalSize + file.size > maxCombinedSize) {
        filesOutsideSizeLimit.push(file);
      } else {
        filesWithinSizeLimit.push(file);
        totalSize += file.size;
      }
    }

    if (filesWithinSizeLimit.length > 0) {
      await uploadAttachment(filesWithinSizeLimit);
    }

    const maxCombinedSizeInMb = maxCombinedSize / 1_000_000;
    if (filesOutsideSizeLimit.length === 1 && filesOutsideSizeLimit[0]) {
      const fileName = filesOutsideSizeLimit[0].name;
      PopupActions.openNotification({
        content: `
          Adding ${fileName} would exceed the ${maxCombinedSizeInMb}MB file size limit. 
          Please remove or compress files and re-upload.
        `,
        title: "File Size Limit Exceeded",
      });
    } else if (filesOutsideSizeLimit.length > 1) {
      PopupActions.openNotification({
        content: `
          The combined size of your attachments is over ${maxCombinedSizeInMb}. Please remove or compress files and re-upload.
        `,
        title: "File Size Limit Exceeded",
      });
    }
    setIsUploading(false);
    if (inputReference.current) {
      inputReference.current.value = "";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
        gap: 1,
        mt: 2,
        width: "100%",
      }}
    >
      <Typography sx={{ color: theme.palette.text.primary }} variant="body2">
        Total Attachment Size:{" "}
        {Math.round(
          100 * (attachments.reduce((accumulator, attachment) => accumulator + attachment.size, 0) / 1_000_000),
        ) / 100}
        MB
      </Typography>
      {attachments && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            marginTop: 1,
          }}
        >
          {attachments.map((attachment) => (
            <Box
              key={attachment.id}
              sx={{
                alignItems: "center",
                backgroundColor: colors.grey[100],
                border: "1px solid",
                borderColor: colors.grey[300],
                borderRadius: 0.5,
                display: "flex",
                gap: 1,
                justifyContent: "space-between",
                p: 1,
                width: "fit-content",
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flex: 1,
                  gap: 1,
                }}
              >
                <FilePresentRoundedIcon sx={{ color: colors.grey[600] }} />
                <Typography sx={{ color: theme.palette.text.primary }} variant="body2">
                  {truncateFilename(attachment.name)}
                </Typography>
              </Box>
              <Tooltip title="Remove Attachment">
                {deleteAttachmentIds.includes(attachment.id) ? (
                  <CircularProgress size={22} />
                ) : (
                  <DeleteRoundedIcon
                    color="error"
                    onClick={() => void removeAttachment(attachment)}
                    sx={{ cursor: "pointer" }}
                  />
                )}
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
      <Button
        color="secondary"
        component="label"
        disabled={isUploading || isDisabled}
        startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        sx={{
          mt: 1,
          width: "fit-content",
        }}
        variant="contained"
      >
        Upload File(s)
        <VisuallyHiddenInput
          multiple
          name="attachments"
          onChange={handleFileChange}
          ref={inputReference}
          type="file"
          value={""}
        />
      </Button>
    </Box>
  );
}

export default AttachmentsManager;
